import { diffWords } from "diff";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import styles from "./approvals.module.css";
import { approvalsService, type ReviewView } from "./approvalsService";

import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Button } from "@/components/Button";
import { ChevronLeftIcon } from "@/components/icons";
import { PageHeader } from "@/components/PageHeader";
import { invalidateNav } from "@/features/manual/api";
import manualStyles from "@/features/manual/manual.module.css";
import { sanitizeManualHtml } from "@/features/manual/sanitize";
import { useCurrentUser } from "@/hooks/useCurrentUser";

/**
 * The review screen: what changed (word-level text diff against the published
 * version) and the approve / reject-with-comment decision. Deliberately a
 * TEXT diff — it answers "what changed?" for an ops manual without structural
 * rich-text diffing.
 */
export function ReviewPage() {
  const { versionId = "" } = useParams();
  const navigate = useNavigate();
  const { csrfToken } = useCurrentUser();
  const [review, setReview] = useState<ReviewView | null>(null);
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState("");
  const [noteError, setNoteError] = useState(false);
  const [outcome, setOutcome] = useState<string | null>(null);

  useEffect(() => {
    approvalsService
      .getReview(versionId)
      .then(setReview)
      .catch(() => setMissing(true));
  }, [versionId]);

  const parts = useMemo(() => {
    if (!review) return [];
    return diffWords(review.publishedText ?? "", review.proposedText);
  }, [review]);

  if (missing) {
    return (
      <div className={manualStyles.view}>
        <div className={manualStyles.emptyState}>
          Nothing to review here — it may already be decided.{" "}
          <Link to="/approvals">Back to the queue</Link>.
        </div>
      </div>
    );
  }
  if (!review) return <div className={`${manualStyles.view} ${manualStyles.quiet}`}>Loading…</div>;

  const approve = async () => {
    setBusy(true);
    try {
      const res = await approvalsService.approve(versionId, csrfToken ?? "");
      invalidateNav();
      setOutcome(
        res.outcome === "published"
          ? "Approved — the new version is live."
          : "Approved — waiting on the second approval."
      );
      setTimeout(() => navigate("/approvals"), 1200);
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!note.trim()) {
      setNoteError(true);
      return;
    }
    setBusy(true);
    try {
      await approvalsService.reject(versionId, note.trim(), csrfToken ?? "");
      setOutcome("Sent back to the editor with your comment.");
      setTimeout(() => navigate("/approvals"), 1200);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`${manualStyles.view} ${manualStyles.viewWide}`}>
      <Breadcrumbs items={[{ label: "Approvals", to: "/approvals" }, { label: review.title }]} />
      <PageHeader
        eyebrow={review.isNewPage ? "New page" : "Change to published page"}
        title={review.title}
      />

      <div className={styles.reviewNote}>
        <strong>{review.authorName ?? "—"}</strong>
        {review.changeSummary ? <> — “{review.changeSummary}”</> : null}
      </div>

      {review.isNewPage ? (
        <>
          <div className={styles.diffLegendRow}>New page — no published version to compare.</div>
          <div
            className={styles.rendered}
            dangerouslySetInnerHTML={{ __html: sanitizeManualHtml(review.html) }}
          />
        </>
      ) : (
        <>
          <div className={styles.diffLegendRow}>
            <span>
              <i className={styles.swatchIns} /> Added
            </span>
            <span>
              <i className={styles.swatchDel} /> Removed
            </span>
          </div>
          <div className={styles.diff}>
            {parts.map((part, i) =>
              part.added ? (
                <ins key={i}>{part.value}</ins>
              ) : part.removed ? (
                <del key={i}>{part.value}</del>
              ) : (
                <span key={i}>{part.value}</span>
              )
            )}
          </div>
        </>
      )}

      {outcome ? (
        <div className={styles.outcome}>{outcome}</div>
      ) : (
        <div className={styles.actions}>
          <Button variant="primary" onClick={() => void approve()} disabled={busy}>
            Approve and publish
          </Button>
          <Button variant="danger" onClick={() => setRejecting(true)} disabled={busy}>
            Reject with comment
          </Button>
          <Link to="/approvals" className={manualStyles.editLink}>
            <ChevronLeftIcon /> Back to queue
          </Link>
        </div>
      )}

      {rejecting && !outcome && (
        <div className={styles.rejectBox}>
          <p>Your comment goes back to {review.authorName ?? "the editor"} with the draft.</p>
          <textarea
            value={note}
            placeholder="What needs to change before this can go live?"
            className={noteError ? styles.noteInvalid : undefined}
            aria-invalid={noteError || undefined}
            aria-describedby={noteError ? "reject-note-error" : undefined}
            onChange={(e) => {
              setNote(e.target.value);
              setNoteError(false);
            }}
          />
          {noteError && (
            <p id="reject-note-error" className={styles.noteError}>
              Add a comment so the editor knows what to fix.
            </p>
          )}
          <div className={styles.rejectActions}>
            <Button variant="danger" onClick={() => void reject()} disabled={busy}>
              Reject
            </Button>
            <Button variant="secondary" onClick={() => setRejecting(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
